import {
  CreateVisitParams,
  FetchSpecificVisitsActionParams,
  FetchVisitResponse,
  FetchVisitSeriesParams,
  FetchVisitSeriesResponse,
  FetchVisitsActionParams,
  FetchVisitsError,
  FetchVisitsResponse,
  GroupVisitParams,
  UpdateVisitActionParams,
  UpdateVisitResponse,
  UpdateVisitSeriesActionParams,
  UpdateVisitSeriesResponse,
  Visit,
  VisitError,
  VisitParams,
  VisitSeriesError,
  VisitStatus,
  VisitUiType,
} from 'store/visits/types';
import { MultiSelectOption } from '@hqo/multi-select';
import { createAction, createAsyncAction } from 'typesafe-actions';

import { FailureActionPayload } from 'store/errors/types';

export const fetchVisits = createAction('visits/FETCH_REQUEST')<FetchVisitsActionParams & { type: VisitUiType }>();

/**
 * Signal that the visit list is stale and should re-read in the background — fired by the activity
 * SSE stream when a change lands elsewhere. A pure data refresh: it never navigates or moves the user
 * (safe while they are in the form or viewing a visit); it only bumps a token the list re-reads on.
 */
export const invalidateVisits = createAction('visits/INVALIDATE')<void>();

export const fetchUpcomingVisits = createAsyncAction(
  'visits/FETCH_UPCOMING_REQUEST',
  'visits/FETCH_UPCOMING_SUCCESS',
  'visits/FETCH_UPCOMING_FAILURE',
)<FetchSpecificVisitsActionParams, FetchVisitsResponse, FetchVisitsError>();

export const fetchPastVisits = createAsyncAction(
  'visits/FETCH_PAST_REQUEST',
  'visits/FETCH_PAST_SUCCESS',
  'visits/FETCH_PAST_FAILURE',
)<FetchSpecificVisitsActionParams, FetchVisitsResponse, FetchVisitsError>();

export const fetchAllVisits = createAsyncAction(
  'visits/FETCH_ALL_REQUEST',
  'visits/FETCH_ALL_SUCCESS',
  'visits/FETCH_ALL_FAILURE',
)<FetchSpecificVisitsActionParams, FetchVisitsResponse, FetchVisitsError>();

export const fetchVisit = createAsyncAction(
  'visits/FETCH_ONE_REQUEST',
  'visits/FETCH_ONE_SUCCESS',
  'visits/FETCH_ONE_FAILURE',
)<VisitParams, FetchVisitResponse, VisitError>();

export const cancelVisit = createAsyncAction('visits/CANCEL_REQUEST', 'visits/CANCEL_SUCCESS', 'visits/CANCEL_FAILURE')<
  VisitParams,
  VisitParams,
  VisitError
>();

export const cancelGroupVisit = createAsyncAction(
  'visits/CANCEL_GROUP_REQUEST',
  'visits/CANCEL_GROUP_SUCCESS',
  'visits/CANCEL_GROUP_FAILURE',
)<GroupVisitParams, GroupVisitParams, FailureActionPayload>();

export const createVisit = createAsyncAction('visits/CREATE_REQUEST', 'visits/CREATE_SUCCESS', 'visits/CREATE_FAILURE')<
  CreateVisitParams,
  Visit,
  FailureActionPayload
>();

export const updateVisit = createAsyncAction('visits/UPDATE_REQUEST', 'visits/UPDATE_SUCCESS', 'visits/UPDATE_FAILURE')<
  UpdateVisitActionParams,
  UpdateVisitResponse,
  VisitError
>();

export const fetchVisitSeries = createAsyncAction(
  'visits/FETCH_SERIES_REQUEST',
  'visits/FETCH_SERIES_SUCCESS',
  'visits/FETCH_SERIES_FAILURE',
)<FetchVisitSeriesParams, FetchVisitSeriesResponse, VisitSeriesError>();

export const updateVisitSeries = createAsyncAction(
  'visits/UPDATE_SERIES_REQUEST',
  'visits/UPDATE_SERIES_SUCCESS',
  'visits/UPDATE_SERIES_FAILURE',
)<UpdateVisitSeriesActionParams, UpdateVisitSeriesResponse, VisitSeriesError>();

export const initializeVisitorRegistration = createAction('INITIALIZE_VISITOR_REGISTRATION')<void>();

export const setSearchFilter = createAction('visits/SET_SEARCH_FILTER')<string>();

export const setStatusFilter = createAction('visits/SET_STATUS_FILTER')<VisitStatus[]>();

export const setCompanyFilter = createAction('visits/SET_COMPANY_FILTER')<MultiSelectOption[]>();

export const setHostFilter = createAction('visits/SET_HOST_FILTER')<MultiSelectOption[]>();

export const clearFilters = createAction('visits/CLEAR_FILTERS')<void>();

export const setFiltersPinned = createAction('visits/SET_FILTERS_PINNED')<boolean>();

export const clearConfirmationCache = createAction('visits/CLEAR_CONFIRMATION_CACHE')<{ visitId: string }>();
